import { Form, Formik } from 'formik';
import * as yup from 'yup';
import { useCallback } from 'react';
import { FlaskConical } from 'lucide-react';

import { notifySuccess } from '@/portainer/services/notifications';
import { ExperimentalFeatures } from '@/react/portainer/settings/types';
import { useUpdateExperimentalSettingsMutation } from '@/react/portainer/settings/queries';
import { useAnalytics } from '@/react/hooks/useAnalytics';

import { LoadingButton } from '@@/buttons/LoadingButton';
import { TextTip } from '@@/Tip/TextTip';

import { EnableOpenAIIntegrationSwitch } from './EnableOpenAIIntegrationSwitch';
import { EnableObservabilitySwitch } from './EnableObservabilitySwitch';

interface FormValues {
  OpenAIIntegration: boolean;
  Observability: boolean;
}
const validation = yup.object({
  OpenAIIntegration: yup.boolean(),
  Observability: yup.boolean(),
});

interface Props {
  settings: ExperimentalFeatures;
}

export function ExperimentalFeaturesSettingsForm({ settings }: Props) {
  const initialValues: FormValues = settings;

  const { trackEvent } = useAnalytics();
  const updateSettingsMutation = useUpdateExperimentalSettingsMutation();

  const handleSubmit = useCallback(
    ({ OpenAIIntegration, Observability }: FormValues) => {
      // if OpenAIIntegration gets enabled or disabled send analytics
      if (!initialValues.OpenAIIntegration && OpenAIIntegration) {
        trackEvent('chatbot-enable-globally', { category: 'portainer' });
      }
      if (initialValues.OpenAIIntegration && !OpenAIIntegration) {
        trackEvent('chatbot-disable-globally', { category: 'portainer' });
      }

      updateSettingsMutation.mutate(
        {
          OpenAIIntegration,
          Observability,
        },
        {
          onSuccess() {
            notifySuccess(
              'Success',
              'Successfully updated experimental features settings'
            );
          },
        }
      );
    },
    [initialValues.OpenAIIntegration, trackEvent, updateSettingsMutation]
  );

  return (
    <Formik<FormValues>
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validation}
      validateOnMount
      enableReinitialize
    >
      {({ isValid, dirty }) => (
        <Form className="form-horizontal">
          <TextTip color="blue" icon={FlaskConical}>
            Experimental features may be discontinued without notice.
          </TextTip>

          <br />
          <br />

          <div className="form-group col-sm-12 text-muted small">
            In Portainer releases, we may introduce features that we&apos;re
            experimenting with. These will be items in the early phases of
            development with limited testing.
            <br />
            Our goal is to gain early user feedback, so we can refine, enhance
            and ultimately make our features the best they can be. Disabling an
            experimental feature will prevent access to it.
          </div>

          <div className="form-group col-sm-12">
            {/* OpenAI Integration Feature */}
            <div className="flex">
              <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-1" />
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900 th-dark:text-white mb-3">
                  OpenAI Integration
                </h4>
                <EnableOpenAIIntegrationSwitch />
                <div className="mt-3">
                  <TextTip color="blue">
                    For more information about the OpenAI integration, see this{' '}
                    <a
                      href="https://www.portainer.io/blog/portainer-chatgpt"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Portainer chatbot blog post
                    </a>
                    .
                  </TextTip>
                </div>
              </div>
            </div>

            <div className="h-px bg-gray-200 my-4" />

            {/* Observability Feature */}
            <div className="flex">
              <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-1" />
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900 th-dark:text-white mb-3">
                  Observability
                </h4>
                <EnableObservabilitySwitch />
              </div>
            </div>
          </div>

          <div className="form-group mt-8">
            <div className="col-sm-12">
              <LoadingButton
                loadingText="Saving settings..."
                isLoading={updateSettingsMutation.isLoading}
                disabled={!isValid || !dirty}
                className="!ml-0"
                data-cy="settings-experimentalButton"
              >
                Save experimental settings
              </LoadingButton>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
}
